// src/pages/Login.jsx
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";

import SEO from "../components/SEO";

export default function Login() {
  const { login } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  const from = location.state?.from?.pathname || "/";

  async function onSubmit({ email, password }) {
    setLoading(true);
    setError("");
    try {
      const cred = await login(email, password);
      // Fetch user profile to determine role-based redirect
      const { getDoc, doc } = await import("firebase/firestore");
      const { db } = await import("../firebase");
      const userDoc = await getDoc(doc(db, "users", cred.user.uid));
      const role = userDoc.exists() ? userDoc.data().role : "parent";

      if (role === "admin") {
        navigate("/admin", { replace: true });
      } else if (role === "doctor") {
        navigate("/doctor", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const msgs = {
        "auth/user-not-found":    lang === "ar" ? "البريد الإلكتروني غير مسجل" : "Email not found",
        "auth/wrong-password":    lang === "ar" ? "كلمة المرور غير صحيحة" : "Wrong password",
        "auth/invalid-credential":lang === "ar" ? "البريد أو كلمة المرور غير صحيحة" : "Invalid email or password",
        "auth/too-many-requests": lang === "ar" ? "تم تجاوز عدد المحاولات، حاول لاحقاً" : "Too many attempts, try later"
      };
      setError(msgs[err.code] || (lang === "ar" ? "حدث خطأ، حاول مجدداً" : "An error occurred, try again"));
    }
    setLoading(false);
  }

  return (
    <div className="page-content fade-in" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1.25rem" }}>
      <SEO 
        title={lang === "ar" ? "تسجيل الدخول" : "Login"} 
        description={lang === "ar" ? "قم بتسجيل الدخول إلى حسابك في منصة نظم." : "Log in to your account on the Nodom platform."}
        canonicalUrl="https://www.nodom.site/login"
      />
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div className="text-center" style={{ marginBottom: "2rem" }}>
          <div style={{ fontSize: "2rem", fontWeight: 800, color: "var(--color-forest)", marginBottom: "0.5rem" }}>نظم</div>
          <h2>{t("login")}</h2>
          <p className="text-muted text-sm">{lang === "ar" ? "أهلاً بك مجدداً" : "Welcome back"}</p>
        </div>

        <div className="card card-lg">
          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group">
              <label className="form-label"><Mail size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />{t("emailLabel")}</label>
              <input
                className={`form-input ${errors.email ? "error" : ""}`}
                type="email"
                dir="ltr"
                placeholder="example@email.com"
                {...register("email", {
                  required: lang === "ar" ? "البريد الإلكتروني مطلوب" : "Email is required",
                  pattern: { value: /^\S+@\S+\.\S+$/, message: lang === "ar" ? "بريد غير صالح" : "Invalid email" }
                })}
              />
              {errors.email && <div className="form-error">{errors.email.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label"><Lock size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />{t("passwordLabel")}</label>
              <div style={{ position: "relative" }}>
                <input
                  className={`form-input ${errors.password ? "error" : ""}`}
                  type={showPass ? "text" : "password"}
                  dir="ltr"
                  placeholder="••••••••"
                  {...register("password", {
                    required: lang === "ar" ? "كلمة المرور مطلوبة" : "Password required",
                    minLength: { value: 6, message: lang === "ar" ? "٦ أحرف على الأقل" : "Minimum 6 characters" }
                  })}
                />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  style={{ position: "absolute", top: "50%", left: lang === "ar" ? "0.75rem" : "auto", right: lang === "ar" ? "auto" : "0.75rem", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password.message}</div>}
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading} style={{ marginTop: "0.5rem" }}>
              {loading ? <div className="spinner" style={{ width: 20, height: 20 }} /> : <><LogIn size={16} />{t("loginBtn")}</>}
            </button>
          </form>

          <div className="divider-text">{lang === "ar" ? "أو" : "or"}</div>

          <p className="text-center text-sm">
            {t("noAccount")}{" "}
            <Link to="/register" style={{ fontWeight: 700 }}>{t("register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
